import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from 'react';
import { Form, Table, Button, Tag, Tooltip, Space } from '@bedrock/components';
import { ChevronRight, Delete, NoticeTextLeft, Plus, PlusCircle } from '@bedrock/icons-react';
import { useControllableValue, useMemoizedFn, useDebounceEffect } from 'ahooks';
import classNames from 'classnames/bind';
import { isEqual, omit, isObject } from 'lodash';
import styles from './index.less';
import TableForm from '#components/TableForm';
import {
  DemandKpiPlatformEnum,
  DemandKpiValueDto,
  DemandGroupType,
  enums,
  outsourceContentMarketQueryGetKpiValues,
  outsourceContentMarketQueryGetKpiValuesImmediately,
} from '#services/api';
import useKPIList from '#features/KPITable/useKPIList';
import { $sType, tableColumnWidthToRem } from '#utils';
import { useCPMCodeList } from '#features/KPITable/useCPMCodeList';
import type { TagProps, TagColor } from '@bedrock/components/lib/Tag';
import ThirdPartyDataModal from './ThirdPartyDataModal'
import { DemandKpiLongOrShortTermEnum } from '#services/api'
import Decimal from 'decimal.js';
import { isBlank } from '@bedrock/utils';

export const getLongOrShortTermTag = (v: DemandKpiLongOrShortTermEnum) => {
  const conf = {
    0: { color: 'light-orange', text: '长期指标' },
    1: { color: 'light-blue', text: '短期指标' },
    2: { color: 'light-grey', text: '未定义' },
  }
  if (!conf[v]) return '--';
  return <Tag size="small" color={conf[v].color as any} text={conf[v].text} />
}

export const DIFF_TYPES = {
  SAME: 'SAME',
  ADD: 'ADD',
  DELETE: 'DELETE', 
  MODIFY: 'MODIFY'
} as const;

export const DIFF_TYPES_COLOR = {
  GREY: 'grey',
  GREEN: 'green',
  RED: 'red', 
  ORANGE: 'orange'
} as const;

const cx = classNames.bind(styles);

const diff_type_map = {
  [DIFF_TYPES.ADD]: {
    text: '新增',
    color: DIFF_TYPES_COLOR.GREEN
  },
  [DIFF_TYPES.DELETE]: {
    text: '删除',
    color: DIFF_TYPES_COLOR.RED
  },
  [DIFF_TYPES.MODIFY]: {
    text: '调整',
    color: DIFF_TYPES_COLOR.ORANGE
  },
  [DIFF_TYPES.SAME]: {
    text: "无调整",
    color: DIFF_TYPES_COLOR.GREY
  }
}

export const checkNoPassKPI = (record: any, onlyNumeric: boolean) => {
  let result = record?.result;
  if (onlyNumeric && record?.kpi_schema?.type !== 'numeric') {
    // 策划复核节点中，直接考核为否的不参与判断该单是否达预期
    return false;
  }
  if (record?.kpi_schema?.type === 'numeric' && !isBlank(record?.actual_value) && !isBlank(record?.expect_value)) {
    // 如果考核指标不存在，且为直接考核指标
    // 且 指标预期和实际值都存在，则判断是否达标
    // 正向指标大于等于指标预期
    if (record?.kpi_schema.positive == 0) {
      result = +record?.actual_value >= +record?.expect_value;
    } else {
      // 负向指标小于等于指标预期
      result = +record?.actual_value <= +record?.expect_value;
    }
  }
  return result === false;
}

type Colkey =
  | 'kpi_code'
  | 'directKpi'
  | 'expect_value'
  | 'show_for_out'
  | 'accept_flag'
  | 'accept_remark'
  | 'actual_value'
  | 'result'
  | 'do'
  | 'expect_after'
  | 'expect_before'
  | 'diff_type'
  | 'short_long_term_indicators';

type ColConfig = {
  key: Colkey;
  view?: boolean;
  show?: boolean;
  title?: string;
  width?: number;
};

export type KPITableProps = {
  groupType?: DemandGroupType;
  view?: boolean;
  colKeys?: (Colkey | ColConfig)[];
  required?: boolean;
  value?: DemandKpiValueDto[];
  onChange?: (value: DemandKpiValueDto[]) => void;
  platforms?: DemandKpiPlatformEnum[];
  moreKpi?: boolean; // 是否允许编辑默认指标，策划设计阶段为默认指标
  detailState?: string;
  forOut?: boolean; // 外包供应商可用
  detailId?: string | number;
  emptyRender?: () => React.ReactNode; // 空数据渲染的dom
  need_actual_execution?: boolean; // 是否实际执行
  $supplier_settle_price?: any; // 供应商填写的结算金额
  cooperate_account_id?: number;
  need_cooperate_account_supplier_settle_price?: boolean; // 是否需要供应商填写结算金额
  actualValueRequired?: boolean;
  structuredVersion?: number;
  product_area?: string;
  applyToTheWholeOrder?: boolean; // 是否应用到整单
  handleApplyToTheWholeOrder?: (value: any[]) => void; // 应用到整单的回调
  /**
   * 禁用「添加指标」按钮 —— 隐藏该按钮但其他字段保持可编辑。
   * 用途：拆单弹窗「已有执行周期」纬度下，禁止增删指标。
   * 与 view 不同 —— view=true 会把所有列都变只读；这里只锁增删。
   */
  disableAdd?: boolean;
  /**
   * 禁用「删除」按钮 —— 隐藏每行删除按钮但其他字段保持可编辑。
   * 用途同 disableAdd。
   */
  disableDelete?: boolean;
  /**
   * 锁定「达标指数 / 指标预期」(expect_value) 列为只读。
   *
   * 用途：拆单「已有执行周期」纬度下，当用户选择的「梯度」属于
   *      preset.existing_gradients（即 (cycle, gradient) 维度的指标
   *      数据已存在），需要禁止用户修改 expect_value；选新梯度时
   *      该 prop 应为 false。
   *
   * 与 view 不同 —— view=true 整表只读；这里只锁定一个单元格列。
   * 与 disableAdd/disableDelete 不同 —— 这两个仅影响行级增删按钮，
   * 而本 prop 影响单元格的可编辑状态。
   */
  disableExpectValueEdit?: boolean;
  /**
   * 锁定「考核指标」(kpi_code) 列为只读 —— 不允许切换指标项。
   *
   * 用途：拆单「已有执行周期 + 已有梯度」纬度下，主要指标行已经由外部
   * 自动注入，不允许用户再通过下拉框修改指标本身。搭配
   * disableAdd/disableDelete/disableExpectValueEdit 一起形成「整行主要
   * 指标只读」的效果，但仍保留「新增指标行」等其他操作能力可由外层控制。
   */
  disableKpiCodeEdit?: boolean;
};

const needResultStates = [
  'PLANNER_ACCEPT',
  'PLANNER_LEADER_ACCEPT',
  'BUSINESS_ACCEPT',
  'DEMANDER_ACCEPT',
  'QC_ACCEPT',
  'APPROVE_SETTLE',
  'SUBMIT_OA',
  'SUBMIT_REIMBURSE',
  'CLOSED',
];

const TrumpNeedResultStates = [
  'PLANNER_ACCEPT',
  'BUSINESS_ACCEPT',
  'DEMANDER_ACCEPT',
  'APPROVE_SETTLE',
  'SUBMIT_OA',
  'CLOSED',
];

const CellContent = function (props) {
  return <div className={cx('cell-content')}>{props.children}</div>;
};

const ViewCellContent = function (props: {
  text: string,
  className?: string;
  tagProps?: TagProps;
  inline?: boolean;
}) {
  if (props.inline) {
    return <span className={cx(props?.className)}>{props?.text}</span>
  }
  return <Tooltip title={props.text}>
    <div className={cx('one-line-ellipsis', { 'one-line-ellipsis-with-tag': !!props?.tagProps }, props?.className)}>
      {props?.tagProps ? <span>{props?.text}</span> : props.text}
      {props?.tagProps && <Tag {...props?.tagProps} />}
    </div>
  </Tooltip>
};


export const KPIValueShowComponent = ({ value, record, inline = false }: { value: any, record: any, inline?: boolean }) => {
  if ([undefined, null, ''].includes(value)) {
    return <ViewCellContent text={'--'} inline={inline} />;
  }
  let showValue = value;
  if (record?.kpi_schema?.percent === 1) {
    showValue = new Decimal(value).mul(100).toString() + '%';
    return showValue;
  }
  return <ViewCellContent text={showValue?.toString?.() || '--'} inline={inline} />;
}

export const kpiSchemaToElement = (text, record) => {
  switch (record?.kpi_schema?.type) {
    case 'numeric':
      const msg = record?.kpi_schema?.percent === 1 ? '百分比只能输入两位小数' : '只能输入4位小数';
      return {
        componentClass: record?.kpi_schema?.percent === 1 ? 'inputPercent' : 'inputNumber',
        rules: [
          {
            message: '不能输入负数',
            validator: (rule, value) => {
              if (value < 0) {
                return Promise.reject('不能输入负数');
              }
              return Promise.resolve();
            },
          },
          {
            message: msg,
            validator: (rule, value) => {
              if (value && value.toString().split('.')[1]?.length > 4) {
                return Promise.reject(msg);
              }
              return Promise.resolve();
            },
          },
        ],
        elementProps: {
          toFixed: 4,
          valueType:"number",
        },
      };
    case 'plain_text':
      return {
        componentClass: 'input',
        elementProps: {
          maxLength: 500,
        },
      };
    default:
      return {
        componentClass: 'input',
        elementProps: {
          maxLength: 500,
        },
      };
  }
};

const KPITable = forwardRef(function (
  props: KPITableProps,
  ref: any
): React.ReactElement<KPITableProps> {
  const {
    groupType = 'CHANNEL_GROUP',
    colKeys,
    required = true,
    view = false,
    platforms = ['MPC'],
    moreKpi = false,
    detailState,
    forOut = false,
    detailId,
    emptyRender,
    need_actual_execution,
    $supplier_settle_price,
    cooperate_account_id,
    need_cooperate_account_supplier_settle_price,
    actualValueRequired,
    structuredVersion,
    applyToTheWholeOrder,
    handleApplyToTheWholeOrder = () => {},
    disableAdd = false,
    disableDelete = false,
    disableExpectValueEdit = false,
    disableKpiCodeEdit = false,
  } = props;

  const [data, setData] = useControllableValue(props, {
    defaultValue: [],
  });

  const isInner = $sType === 'inner';
  const KPIList = useKPIList(!view || moreKpi, detailId);
  const tableFormRef = React.useRef(null);
  const [localData, setLocalData] = useState([]);
  const [needGetCPM, setNeedGetCPM] = useState(false);
  const { checkCPM } = useCPMCodeList({
    detailId,
    groupType,
  });

  useDebounceEffect(
    () => {
      if (isEqual(data, localData)) {
        return;
      } else {
        data && tableFormRef?.current?.form?.setFieldsValue?.(data || []);
      }
    },
    [data, localData],
    {
      leading: false,
      trailing: true,
      wait: 40,
    }
  );
  const propsColKeyMap =
    colKeys?.reduce((acc, item) => {
      if(isObject(item)) {
        acc[item.key] = item
      }
      return acc;
    }, {}) || {};

  const showColKeys = React.useMemo(() => {
    const propsColKeys =
      colKeys?.reduce((acc, item) => {
        if (typeof item === 'string') {
          acc.push(item);
        } else {
          if (item.show !== false) {
            acc.push(item.key);
          }
        }
        return acc;
      }, []) || [];
    let showColKeys = ['kpi_code', 'expect_value', ...propsColKeys];
    switch (groupType) {
      case 'CD_TRUMPET_GROUP':
        if (platforms.includes('MPC')) {
          showColKeys.push(...['directKpi'])
        }
        if (needResultStates.includes(detailState?.toUpperCase())) {
          showColKeys.push('actual_value', 'result');
        }
        break;

      default:
        if (platforms.includes('MPC')) {
          showColKeys.push(...['directKpi', 'show_for_out']);

          if (structuredVersion >= 1) {
            showColKeys.push(...['accept_flag', 'accept_remark']);
          }
        }
        if (view) {
          // 删除 accept_flag
          showColKeys = showColKeys.filter((item) => item !== 'accept_flag')
        }
        if (['MAKING'].includes(detailState?.toUpperCase())) {
          showColKeys.push('actual_value');
        }
        if (needResultStates.includes(detailState?.toUpperCase())) {
          showColKeys.push('actual_value', 'result');
        }
        if (!isInner) {
          // 外包供应商隐藏，show_for_out和result
          const hideKeys = ['show_for_out', 'result'];
          showColKeys = showColKeys.filter((item) => !hideKeys.includes(item));
        }
        break;
    }

    showColKeys = showColKeys.filter(
      (item) => colKeys?.find((n) => n?.key === item)?.show !== false
    );

    if (!view || moreKpi) {
      showColKeys.push('do');
    }
    return showColKeys;
  }, [view, moreKpi, detailState, platforms, structuredVersion]);

  const getColConfig = useMemoizedFn((colkey: Colkey, recode?: any) => {
    let colConfig = {
      view: view,
    };
    // 绑定执行数据为否，不能编辑
    if (need_actual_execution === false) {
      colConfig.view = true;
      return colConfig;
    }
    // 如果是默认指标，且在非默认指标情况下，只能编辑非默认指标
    if (moreKpi && recode?.custom === 1) {
      colConfig.view = false;
      // 供应商可见除外
      if (colkey === 'show_for_out') {
        colConfig.view = true;
      }
    }
    // 单独配置
    let singleColConfig =
      colKeys?.find((item: any) => item?.key === colkey) || {};
    Object.assign(colConfig, omit(singleColConfig, ['key']));

    // 指标结果的编辑逻辑
    if (colkey === 'actual_value' && !colConfig.view) {
      // 如果外包系统，则不能编辑 供应商不可见的指标
      if ($sType === 'outer' && !recode?.show_for_out) {
        colConfig.view = true;
      }
      // 如果非外包系统，则不能编辑 供应商可见的指标
      if ($sType === 'inner' && recode?.show_for_out) {
        colConfig.view = true;
      }
    }

    // 是否达预期的编辑逻辑
    if (colkey === 'result' && !colConfig.view) {
      // 只能编辑非直接考核的指标
      if (recode?.kpi_schema?.type === 'numeric') {
        colConfig.view = true;
      }
    }
    // moreKpi 下，直接考核指标的编辑逻辑
    if (colkey === 'result' && moreKpi) {
      colConfig.view = true;
    }

    // 外包系统 & 产品区域为海外 & 指标类型为CPM可编辑、不通过接口获取
    const OUTER_OVERSEAS = $sType === 'outer' && props.product_area === 'OVERSEAS';

    // CPM 指标的指标结果不可编辑，无论任何场景
    if (checkCPM(recode?.kpi_schema?.code) && !OUTER_OVERSEAS) {
      if (['actual_value', 'result']?.includes(colkey)) {
        if (colConfig.view === false) {
          // 如果CPM指标，则不能编辑，而是通过接口获取
          setNeedGetCPM(true);
        }
        colConfig.view = true;
      }
    }
    return colConfig;
  });

  const updateCPMRequest = () => {
    if (need_cooperate_account_supplier_settle_price) {
      return outsourceContentMarketQueryGetKpiValuesImmediately({
        case_demand_id: +detailId,
        cooperate_account_id: +cooperate_account_id,
        supplier_settle_price: $supplier_settle_price?.value || 0,
      });
    }
    return outsourceContentMarketQueryGetKpiValues({
      case_demand_id: +detailId,
    });
  };

  const updateCPM = useMemoizedFn(() => {
    updateCPMRequest().then((result) => {
      if (result.code === 1) {
        const resData = result?.data || [];
        const dataMap = resData.reduce((acc, item) => {
          acc[item.demand_cooperate_account_id + item.kpi_code] = item;
          return acc;
        }, {});
        const newData = [...data];
        newData.forEach((item) => {
          const v = dataMap[item?.demand_cooperate_account_id + item?.kpi_code];
          if (item && v) {
            item.actual_value = v.actual_value;
            item.label = v.label;
            item.label_obj = v.label_obj;
          }
        });
        setData(newData);
        setLocalData(newData);
        tableFormRef.current?.form?.setFieldsValue(newData);
      }
    });
  });

  useDebounceEffect(
    () => {
      if (needGetCPM) {
        // 获取CPM指标
        updateCPM();
      }
    },
    [needGetCPM, $supplier_settle_price?.value],
    { wait: 500 }
  );

  const [colSpan, setColSpan] = useState(5);

  const ableDelete = React.useMemo(() => {
    if (!required) return true;
    return data?.length > 1;
  }, [data, required]);

  const addItem = useMemoizedFn(() => {
    tableFormRef.current?.add?.({
      accept_flag: false,
      custom: moreKpi ? 1 : 0,
    });
  });

  useDebounceEffect(
    () => {
      if (required && !data?.length && !view) {
        addItem();
      }
    },
    [data, required],
    {
      leading: false,
      trailing: true,
      wait: 40,
    }
  );

  useImperativeHandle(ref, () => ({
    add: tableFormRef.current?.add,
    remove: tableFormRef.current?.remove,
    form: tableFormRef.current?.form,
    validateFields: () => {
      if (!tableFormRef.current?.form?.validateFields) {
        return Promise.resolve();
      }
      return tableFormRef.current?.form?.validateFields().catch((error) => {
        return Promise.reject(undefined);
      });
    },
  }));

  const setIndexValue = useMemoizedFn((index, value) => {
    const newData = [...data];
    newData[index] = {
      ...newData[index],
      ...value,
    };
    setData(newData);
    tableFormRef.current?.form?.setFieldsValue?.(newData);
  });

  const kpiCodeOptions = useMemo(() => {
    let schemaKPIList = KPIList;
    if (!schemaKPIList?.length) {
      schemaKPIList = data?.map((item) => item?.kpi_schema) || [];
    }
    schemaKPIList = schemaKPIList?.filter((item) =>
      platforms?.includes?.(item?.platform)
    );
    return (
      schemaKPIList?.map((item) => {
        return {
          name: item?.name,
          value: item?.code,
          // 已经选择的考核指标，不可再次选择
          disabled: !!data?.some((n) => n?.kpi_code === item?.code),
        };
      }) || []
    );
  }, [KPIList, view, data, platforms]);

  if (emptyRender) {
    if (view && !moreKpi && !data?.length) {
      return (emptyRender?.() || null) as any;
    }
  }

  const getActualValueRequired = () => {
    if (typeof actualValueRequired === 'boolean') {
      return actualValueRequired;
    }
    return need_actual_execution === false ? false : true;
  };

  return (
    <div
      style={{
        width: '100%',
        flex: '1 1 auto',
        overflow: 'hidden',
      }}
    >
      <TableForm
        className={cx('kpi-table')}
        scroll={{ x: 500 }}
        ref={tableFormRef}
        value={data}
        emptyText="暂无考核指标"
        formProps={{}}
        onChange={(value) => {
          setData(value);
          setLocalData(value);
        }}
        formColumns={({ add, remove }) => {
          let columns = [
            {
              title: '考核指标',
              dataIndex: 'kpi_code',
              minWidth: 160,
              width: propsColKeyMap['kpi_code']?.width || null,
              fixed: 'left',
              // disableKpiCodeEdit 命中时整列只读 → required 无意义
              required: !disableKpiCodeEdit && !getColConfig('kpi_code')?.view,
              align: 'left',
              formItem: (text, record: DemandKpiValueDto, index) => {
                const config = getColConfig('kpi_code', record);
                return {
                  componentClass: 'select',
                  ...config,
                  // disableKpiCodeEdit：已有周期纬度下主要指标由外部自动注入，
                  // 不允许用户切换指标本身 → 展示 disabled 状态的选择框（而非纯文本）
                  elementProps: {
                    showSearch: true,
                    options: kpiCodeOptions,
                    disabled: disableKpiCodeEdit || undefined,
                    dropdownStyle: {
                      width: 240,
                    },
                    onChange: (value) => {
                      let kpi_schema = KPIList.find(
                        (item) => item.code === value
                      );
                      setIndexValue(index, {
                        expect_value: '',
                        kpi_code: value,
                        kpi_schema,
                        show_for_out: moreKpi
                          ? false
                          : kpi_schema?.show_for_out == 1,
                      });
                    },
                    showComponent() {
                      if (!text) return '--'
                      let kpi_schema = record.kpi_schema || KPIList.find((item) => item.code === text);
                      let tagProps: TagProps;
                      if (structuredVersion >= 1 && record?.accept_flag) {
                        tagProps = { text: '验收标准', color: 'orange', size: 'small' };
                      }
                      return <ViewCellContent text={kpi_schema?.name} className={cx('bold')} tagProps={tagProps} />
                    }
                  }
                }
              },
            },
            {
              title: '直接考核',
              dataIndex: 'directKpi',
              width: 120,
              align: 'left',
              render: (text, record, index) => {
                const data = enums.DemandKpiPositiveEnum.pick(
                  record?.kpi_schema?.positive
                );
                const config =
                  data?.value === 0
                    ? {
                      color: 'green',
                      tip: '>=指标预期为达预期',
                    }
                    : {
                      color: 'red',
                      tip: '<=指标预期为达预期',
                    };
                return (
                  <CellContent>
                    <Space size={[8, 0]}>
                      {record?.kpi_schema?.type == 'numeric' ? '是' : '否'}
                      {record?.kpi_schema?.type == 'numeric' ? (
                        <Tooltip title={config.tip}>
                          <Tag
                            size="small"
                            text={data?.name}
                            color={`light-${config.color}`}
                          />
                        </Tooltip>
                      ) : (
                        ''
                      )}
                    </Space>
                  </CellContent>
                );
              },
            },
            {
              title: '短期/长期指标',
              dataIndex: 'short_long_term_indicators',
              align: 'left',
              width: 120,
              hidden: true,
              formItem: (text, record) => {
                return {
                  componentClass: 'input',
                  ...getColConfig('short_long_term_indicators', record),
                  view: true,
                  elementProps: {
                    showComponent() {
                      return getLongOrShortTermTag(record?.kpi_schema?.long_or_short_term)
                    },
                  },
                };
              },
            },
            {
              title: propsColKeyMap['expect_value']?.title ? propsColKeyMap['expect_value']?.title : platforms.includes('MPC') ? '指标预期' : '达标指数',
              dataIndex: 'expect_value',
              width: 280,
              align: 'left',
              // disableExpectValueEdit 命中时，整列只读 → required 也无意义
              required: !disableExpectValueEdit && !getColConfig('expect_value')?.view,
              formItem: (text, record, index) => {
                if (!record.kpi_schema) {
                  return {
                    view: view || disableExpectValueEdit,
                  };
                }
                let config = kpiSchemaToElement(text, record);
                return {
                  ...config,
                  ...getColConfig('expect_value', record),
                  // disableExpectValueEdit：拆单「已有梯度」纬度下，
                  // 展示 disabled 状态的输入控件（而非纯文本），与考核指标列保持一致
                  elementProps: {
                    placeholder: '请输入',
                    showComponent() {
                      return <KPIValueShowComponent value={text} record={record} />;
                    },
                    ...config.elementProps,
                    ...(disableExpectValueEdit ? { disabled: true } : {}),
                  },
                };
              },
            },
            {
              title: '指标结果',
              dataIndex: 'actual_value',
              minWidth: 250,
              align: 'left',
              required: getColConfig('actual_value').view ? false: getActualValueRequired(),
              formItem: (text, record: DemandKpiValueDto, index) => {
                const { actual_value, actual_value_extra, $valueType, cpm } = record
                let randerText
                // 内包
                if (isInner) {
                  // cpm 取新字段
                  if (cpm) {
                    randerText = actual_value_extra
                  } else {
                    randerText = actual_value
                  }
                } else {
                  // 外包
                  randerText = actual_value
                }

                // 含三方数据-弹框打开
                if ($valueType) {
                  if ($valueType == 'actual_value_extra') {
                    randerText = actual_value_extra
                  } else {
                    randerText = actual_value
                  }
                }


                if (!record.kpi_schema) {
                  return {};
                }
                let config = kpiSchemaToElement(randerText, record);
                return {
                  ...config,
                  view,
                  ...getColConfig('actual_value', record),
                  elementProps: {
                    ...config.elementProps,
                    placeholder: '请输入',
                    showComponent() {
                      if (typeof randerText === 'undefined') return '--';
                      return (
                        <Space size={[8, 8]} wrap className={cx('max100')}>
                          <KPIValueShowComponent value={randerText} record={record} />
                          {record.label_obj?.label_name && (
                            <Tag
                              color="light-red"
                              text={record.label_obj?.label_name}
                              size="small"
                            />
                          )}

                          {/* 查看不含三方数据按钮 区域 - 新数据才显示 内包才显示 */}
                          {isInner && typeof record?.actual_value_extra == 'string' && (record.actual_value !== record.actual_value_extra) && record.$isShowThirdDataText !== false && (
                            <Button onClick={() => {
                              ThirdPartyDataModal.show({
                                ...props,
                                view: true,
                                moreKpi: false,
                                value: record,
                                colKeys: [],
                                title: '查看不含三方数据'
                              })
                            }}
                              type="link" suffixIcon={<ChevronRight />}>
                              查看不含三方数据
                            </Button>
                          )}


                          {/* 含三方数据 tag显示 */}
                          {record.$valueType == 'actual_value_extra' && <Tag
                            text="含三方数据"
                            color='light-green'
                          ></Tag>}
                          {/* 不含三方数据 tag显示 */}
                          {record.$valueType == 'actual_value' && <Tag
                            text="不含三方数据"
                            color='light-red'
                          ></Tag>}
                        </Space>
                      );
                    },
                  },
                };
              },
            },
            {
              title: '调整类型',
              dataIndex: 'diff_type',
              align: 'left',
              width: 80,
              hidden: true,
              formItem: (text, record) => {
                return {
                  componentClass: 'input',
                  ...getColConfig('diff_type', record),
                  view: true,
                  elementProps: {
                    showComponent() {
                      return (
                          <Tag
                            color={`light-${diff_type_map?.[text]?.color}` as TagColor}
                            text={diff_type_map?.[text]?.text}
                            size="small"
                          />
                      ) 
                    },
                  },
                };
              },
            },
            {
              title: propsColKeyMap?.['expect_after']?.title || '达标指数(调整后)',
              dataIndex: 'expect_after',
              hidden: true,
              width: 180,
              align: 'left',
              required: !getColConfig('expect_after')?.view,
              formItem: (text, record, index) => {
                if (!record.kpi_schema) {
                  return {
                    view,
                  };
                }
                let config = kpiSchemaToElement(text, record);
                return {
                  ...config,
                  ...getColConfig('expect_after', record),
                  elementProps: {
                    ...config.elementProps,
                    placeholder: '请输入',
                    showComponent() {
                      return <span style={['ADD','MODIFY']?.includes(record?.diff_type) ? { color: 'var(--status-warning-1)' } : {}}><KPIValueShowComponent value={text} record={record} /></span>
                    },
                  },
                };
              },
            },
             {
              title: '达标指数(调整前)',
              dataIndex: 'expect_before',
              width: 180,
              align: 'left',
              hidden: true,
              required: !getColConfig('expect_before')?.view,
              render: (text, record) => <CellContent><KPIValueShowComponent value={text} record={record} /></CellContent>
            },
            {
              title: '供应商可见',
              dataIndex: 'show_for_out',
              align: 'left',
              width: 104,
              hidden: true,
              formItem: (text, record, index) => {
                return {
                  componentClass: 'singleCheckbox',
                  ...getColConfig('show_for_out', record),
                  elementProps: {
                    children: "可见",
                    disabled: view,
                  },
                };
              },
            },
            {
              title: '是否验收标准',
              dataIndex: 'accept_flag',
              align: 'left',
              width: 148,
              formItem: (text, record, index) => {
                return {
                  componentClass: 'singleCheckbox',
                  ...getColConfig('accept_flag', record),
                  elementProps: {
                    children: '验收标准',
                    onChange: (checked) => {
                      if (checked) {
                        setIndexValue(index, {
                          accept_flag: true,
                          // show_for_out: true,
                        });
                      } else {
                        setIndexValue(index, {
                          accept_flag: false,
                          accept_remark: '',
                        });
                      }
                    },
                  },
                };
              },
            },
            {
              title: '验收标准备注',
              dataIndex: 'accept_remark',
              align: 'left',
              width: 280,
              required: false,
              formItem: (v, record, index) => {
                return {
                  componentClass: 'input',
                  ...getColConfig('accept_remark', record),
                  elementProps: {
                    disabled: !record.accept_flag,
                    maxLength: 200,
                  },
                };
              },
            },
            {
              title: '是否达预期',
              dataIndex: 'result',
              align: 'left',
              width: 150,
              fixed: 'right',
              required:
                typeof actualValueRequired === 'boolean'
                  ? actualValueRequired
                  : !getColConfig('result')?.view,
              formItem: (text, record, index) => {
                return {
                  view,
                  ...getColConfig('result', record),
                  componentClass: 'select',
                  elementProps: {
                    placeholder: '请选择',
                    options: [
                      {
                        name: '达预期',
                        value: true,
                      },
                      {
                        name: '未达预期',
                        value: false,
                      },
                    ],
                    showComponent() {
                      if (need_actual_execution === false) {
                        return <CellContent>--</CellContent>;
                      }
                      const { result: row_result, result_extra: row_result_extra, $valueType } = record;
                      let result = row_result

                      // 内包
                      if (isInner) {
                        // 不是弹窗打开
                        if (!$valueType) {
                          result = row_result
                        }
                        // 弹窗打开
                        else {
                          result = row_result
                        }
                      }
                      // 外包显示 不含三方的数据
                      else {
                        result = row_result
                      }

                      let kpi_schema =
                        record.kpi_schema ||
                        KPIList.find((item) => item.code === record.kpi_code);
                      // 实际结果可以编辑
                      const actualIsEdit =
                        getColConfig('actual_value', record)?.view === false;
                      if (typeof result === 'undefined' || actualIsEdit) {
                        // 如果考核指标不存在，且为直接考核指标
                        // 且 指标预期和实际值都存在，则判断是否达标
                        if (
                          kpi_schema?.type === 'numeric' &&
                          ['expect_value', 'actual_value'].every(
                            (n) => typeof n !== 'undefined'
                          )
                        ) {
                          // 正向指标大于等于指标预期
                          if (kpi_schema.positive == 0) {
                            result = +record.actual_value >= +record.expect_value;
                          } else {
                            // 负向指标小于等于指标预期
                            result = +record.actual_value <= +record.expect_value;
                          }
                        } else {
                          return <CellContent>--</CellContent>;
                        }
                      }
                      if (result) {
                        return (
                          <CellContent>
                            <Tag size="xSmall" color="green" text="达预期" />
                          </CellContent>
                        );
                      }
                      return (
                        <CellContent>
                          <Tag size="xSmall" color="red" text="未达预期" />
                        </CellContent>
                      );
                    },
                  },
                };
              },
            },
            {
              title: '操作',
              dataIndex: 'do',
              align: 'center',
              width: 64,
              fixed: 'right',
              render: (text, record, index) => {
                const config = getColConfig('kpi_code', record);
                return (
                  <Button
                    danger
                    // disableDelete：拆单「已有执行周期」纬度下禁止删除指标行
                    disabled={!ableDelete || config.view || disableDelete}
                    type="link"
                    onClick={() => {
                      remove(index);
                    }}
                    style={{ marginTop: 4 }}
                  >
                    删除
                  </Button>
                );
              },
            },
          ]
            .filter((column) => showColKeys?.includes(column.dataIndex))
            .map(tableColumnWidthToRem);
          setColSpan(columns?.length);
          return columns;
        }}
      />
      {need_actual_execution !== false && (!view || moreKpi) && !disableAdd ? (
        // disableAdd：拆单「已有执行周期」纬度下隐藏「添加指标」按钮
        <Space size='small' className='m-t-8'>
          <Button
            icon={<Plus />}
            type="checked"
            size='small'
            onClick={() => {
              addItem();
            }}
          >
            添加指标
          </Button>
          {applyToTheWholeOrder && <Button
            size='small'
            icon={<NoticeTextLeft />}
            type="checked-neutral"
            onClick={() => handleApplyToTheWholeOrder(data)}
          >
            应用至整单
          </Button>}
        </Space>
      ) : null}
    </div>
  );
});

export default KPITable;
